import {Component, computed, inject, input, output} from '@angular/core';
import {ButtonModule} from 'primeng/button';
import {TooltipModule} from 'primeng/tooltip';
import {Divider} from 'primeng/divider';
import {firstValueFrom} from 'rxjs';
import {
  ContractParametersContainerResponse,
  ContractParametersResponse,
  ContractParametersUpsertContainerRequest,
  ImplementationTimelineUpsertRequest,
  UpsertContractParametersContainerRequestParams,
} from '@frontend/shared';
import {ContractParametersService} from '../../contract-parameters.service';
import {ContractParametersComponent} from '../../contract-parameters/contract-parameters.component';
import {ProjectScopeComponent} from '../../project-scope/project-scope.component';
import {ContractImplementationTimelineComponent} from '../../implementation-timeline/contract-implementation-timeline.component';
import {AppPanelComponent} from '../../../../../../../../shared/components/header/panel/app-panel.component';
import {ToastService} from '@frontend/toast';
import {ImplementationTimelineContainerData} from '../../implementation-timeline/implementation-timeline-container.types';

@Component({
  selector: 'app-proposal-parameter-panels',
  imports: [
    ButtonModule,
    TooltipModule,
    Divider,
    ContractParametersComponent,
    ProjectScopeComponent,
    ContractImplementationTimelineComponent,
    AppPanelComponent,
  ],
  templateUrl: './proposal-parameter-panels.component.html',
})
export class ProposalParameterPanelsComponent {
  private readonly contractParametersService = inject(ContractParametersService);
  private readonly toastService = inject(ToastService);

  readonly projectId = input.required<number>();
  readonly contractParametersContainer = input.required<ContractParametersContainerResponse>();
  readonly isReadOnly = input<boolean>(false);

  readonly saved = output<void>();

  readonly contractParameters = computed(
    () => this.contractParametersContainer().contractParameters,
  );

  readonly implementationTimelineData = computed<ImplementationTimelineContainerData>(() =>
    this.contractParametersService.mapToImplementationTimelineContainerData(
      this.contractParametersContainer().implementationTimelines,
    ),
  );

  async handleParametersSubmit(newValues: ContractParametersResponse): Promise<void> {
    const merged: ContractParametersResponse = {
      ...this.contractParameters(),
      contractTotalTerms: newValues.contractTotalTerms,
      terminationPeriod: newValues.terminationPeriod,
      solutionType: newValues.solutionType,
      implementationMethod: newValues.implementationMethod,
      licenseModel: newValues.licenseModel,
      paymentTerm: newValues.paymentTerm,
    };
    await this.submit(merged, []);
  }

  async handleScopeSubmit(newValues: ContractParametersResponse): Promise<void> {
    const merged: ContractParametersResponse = {
      ...this.contractParameters(),
      languages: newValues.languages,
    };
    await this.submit(merged, []);
  }

  async handleImplementationTimelineSubmit(
    newValues: ImplementationTimelineContainerData,
  ): Promise<void> {
    const upsertRequests: ImplementationTimelineUpsertRequest[] = newValues.data.map(
      (item) =>
        ({
          projectId: this.projectId(),
          officeId: item.officesLookupResponse.id,
          directusModuleFunctionId: item.directusModuleFunctionId,
          goLiveDate: item.goLiveDate,
          contractExpiryDate: item.contractExpiryDate,
        }) as ImplementationTimelineUpsertRequest,
    );
    await this.submit({...this.contractParameters()}, upsertRequests);
  }

  private async submit(
    contractParameters: ContractParametersResponse,
    implementationTimelines: ImplementationTimelineUpsertRequest[],
  ): Promise<void> {
    const request: UpsertContractParametersContainerRequestParams = {
      projectId: this.projectId(),
      contractParametersUpsertContainerRequest: {
        contractParameters,
        implementationTimelines,
      } as ContractParametersUpsertContainerRequest,
    };
    try {
      await firstValueFrom(
        this.contractParametersService.upsertContractParametersContainer(request),
      );
      void this.toastService.showSuccessMessage();
      this.saved.emit();
    } catch (err) {
      console.error('Update (Contract Parameters) failed:', err);
    }
  }
}
